import {Card} from 'platform/components';
import {Box} from 'platform/foundation';

import {Dispatch, SetStateAction} from 'react';
import {useSelector} from 'react-redux';

import {isNil, isNotNil} from 'ramda';

import {DrillDownLevelName, selectTenant, SourcingVehicleDetailResponseBody} from '@dms/api/core';
import {i18n} from '@dms/i18n';
import {testIds} from '@dms/routes';
import {DrillDown} from '@dms/shared';

import {buildObject} from 'shared/web';

import {DEFAULT_CURRENCY_CODE} from '../../../../constants/DEFAULT_CURRENCY_CODE';
import {useSourcingCurrency} from '../../../../hooks/useSourcingCurrency';
import {useSourcingTargetCountry} from '../../../../hooks/useSourcingTargetCountry';
import {isUnifiedSourcingEnabled} from '../../../../utils/isUnifiedSourcingEnabled';
import {useDrillDownStockUrlLoader} from '../hooks/useDrillDownStockUrlLoader';
import {getDrillDownSelectedRow} from '../utils/getDrillDownSelectedRow';

// The vehicle fixes these levels through the permanent filters.
const HIDDEN_LEVELS: DrillDownLevelName[] = ['make', 'model_family_group', 'model'];

interface DrillDownTabProps {
  vehicle?: SourcingVehicleDetailResponseBody;
  setFullScreen: Dispatch<SetStateAction<boolean>>;
}

export function DrillDownTab(props: DrillDownTabProps) {
  const {data: selectedTenant} = useSelector(selectTenant);
  const tenantCountry = selectedTenant?.country;
  const currency = useSourcingCurrency()?.code;
  const country = useSourcingTargetCountry()?.code ?? tenantCountry;
  const getStockUrlLoader = useDrillDownStockUrlLoader({
    vehicle: props.vehicle,
    hiddenLevels: HIDDEN_LEVELS,
    countryCode: country,
    currency,
  });

  if (isNil(props.vehicle) || isNil(country) || isNil(currency)) {
    return null;
  }

  const permanentFilters = buildObject<Partial<Record<DrillDownLevelName, string>>>({})
    .make(props.vehicle.sourcingVehicle.make?.key, isNotNil(props.vehicle.sourcingVehicle.make))
    .model_family_group(
      props.vehicle.sourcingVehicle.modelFamilyGroup?.key,
      isNotNil(props.vehicle.sourcingVehicle.modelFamilyGroup)
    )
    .model(props.vehicle.sourcingVehicle.model?.key, isNotNil(props.vehicle.sourcingVehicle.model))
    .build();

  return (
    <Card
      title={i18n.t('general.labels.drillDown')}
      tooltip={i18n.t('entity.vehicle.labels.drillDownTooltip')}
      actions={[
        {
          type: 'button',
          variant: 'link',
          title: i18n.t('general.labels.allMakes'),
          leftIcon: 'action/zoom_in',
          onClick: () => props.setFullScreen(true),
        },
      ]}
    >
      {/* Bounded height makes this the vertical scroll container too, so the sticky table header
          pins to it instead of scrolling away with the card. */}
      <Box overflowX="auto" overflowY="auto" maxHeight="70vh">
        <DrillDown
          hasUnifiedSurfaces={isUnifiedSourcingEnabled()}
          regions={[country]}
          currency={currency ?? DEFAULT_CURRENCY_CODE}
          hiddenLevels={HIDDEN_LEVELS}
          permanentFilters={permanentFilters}
          selectedRow={getDrillDownSelectedRow(props.vehicle)}
          getStockUrlLoader={getStockUrlLoader}
          hideConfigurator
          data-testid={testIds.sourcing.vehicleDetail('drillDownTab-drillDown')}
        />
      </Box>
    </Card>
  );
}
